import * as React from 'react'

export const useCallbackRef: () => [
  React.MutableRefObject<HTMLElement | null>,
  (HTMLElement) => void
] = () => {
  const elRef = React.useRef<HTMLElement | null>(null)

  const setElRefCallback = React.useCallback((el) => {
    if (el) elRef.current = el
  }, [])

  return [elRef, setElRefCallback]
}

export const useCallbackRefState: () => [
  HTMLElement | null,
  (HTMLElement) => void
] = () => {
  const [elRef, setElRef] = React.useState<HTMLElement | null>(null)
  const hasElement = React.useRef(false)

  const setElRefCallback = React.useCallback((el) => {
    // Commit the attached node to state exactly once — the first time one
    // becomes available — and never again. Under React 19 concurrent rendering
    // with heavy CPU contention, renders get interrupted and re-run, so the DOM
    // node attached via the callback ref churns: React commits and replaces the
    // node, firing this callback with a fresh identity each time. The previous
    // `prev === el ? prev : el` guard couldn't catch that — a new identity is
    // never `=== prev` — so every churn scheduled another render, which churned
    // the node again: an unbounded "Maximum update depth exceeded" loop.
    //
    // A one-shot setState makes the returned element reference stable and the
    // loop structurally impossible (later invocations are no-ops). All consumers
    // attach this to a single, always-mounted element, so the first node is the
    // node — there is no element swap to miss.
    if (el && !hasElement.current) {
      hasElement.current = true
      setElRef(el)
    }
  }, [])

  return [elRef, setElRefCallback]
}
